import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Textarea } from '../../components/ui/Form';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { mealRequestsAPI } from '../../services/api';

const mealRequestSchema = z.object({
  mealDate: z.string().min(1, 'Meal date is required'),
  classCounts: z.record(z.string(), z.number().min(0)).refine(
    (counts) => Object.values(counts).some((count) => count > 0),
    'At least one class must have a positive number of students'
  ),
  specialRequirements: z.string().optional(),
  notes: z.string().optional(),
});

const defaultValues = {
  mealDate: new Date().toISOString().split('T')[0],
  classCounts: {
    ECD: 0,
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0,
    '6': 0,
    '7': 0,
    '8': 0,
    '9': 0,
    '10': 0,
  },
  specialRequirements: '',
  notes: '',
};

const Dashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(mealRequestSchema),
    defaultValues,
  });

  useEffect(() => {
    if (user?.is_school_user) {
      reset(defaultValues);
    }
  }, [user, reset]);

  const classCounts = watch('classCounts');

  const onSubmit = async (data) => {
    if (!user?.school_id) {
      toast.error('Unable to submit meal request', 'No school assigned to this user');
      return;
    }

    setSubmitting(true);

    try {
      await mealRequestsAPI.createRequest({
        school_id: user.school_id,
        date: data.mealDate,
        class_counts: data.classCounts,
        remarks: [data.specialRequirements, data.notes].filter(Boolean).join(' | '),
      });

      toast.success('Meal request submitted', 'Your meal request has been sent');
      reset(defaultValues);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit meal request';
      toast.error('Request failed', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user?.is_school_user) {
    return (
      <div className="py-10 px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <CardTitle>Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              The dashboard is available only for school users. If your account has
              <span className="font-semibold"> is_school_user: true</span>, the meal request form will appear here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Meal Request Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label>School</Label>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {user.school_name || user.school_id || 'No school assigned'}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mealDate">Meal date</Label>
                <Controller
                  name="mealDate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="date"
                      {...field}
                      id="mealDate"
                      error={errors.mealDate?.message}
                    />
                  )}
                />
                {errors.mealDate && <p className="text-sm text-red-600">{errors.mealDate.message}</p>}
              </div>

              <div>
                <Label>Class counts</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  {Object.entries(classCounts || {}).map(([className, count]) => (
                    <div key={className} className="space-y-2">
                      <Label className="text-sm">{className}</Label>
                      <Controller
                        name={`classCounts.${className}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            min="0"
                            value={field.value}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                  ))}
                </div>
                {errors.classCounts && (
                  <p className="text-sm text-red-600 mt-2">{errors.classCounts.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialRequirements">Special requirements</Label>
                <Controller
                  name="specialRequirements"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="specialRequirements"
                      rows={3}
                      placeholder="Optional special requirements"
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="notes"
                      rows={3}
                      placeholder="Optional notes for the meal request"
                    />
                  )}
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" loading={submitting} className="w-full sm:w-auto">
                  Submit meal request
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
